import React, { useState, useMemo } from 'react';
import * as LucideIcons from 'lucide-react';
import { Category } from '../types';

// Helper type for Lucide icon components
type LucideIcon = React.ForwardRefExoticComponent<
  Omit<React.SVGProps<SVGSVGElement>, 'ref'> & {
    size?: number | string;
    className?: string;
    strokeWidth?: number | string;
  } & React.RefAttributes<SVGSVGElement>
>;

interface Props {
  category: Category;
  onClick: () => void;
}

export const CategoryCard: React.FC<Props> = ({ category, onClick }) => {
  const IconComponent = useMemo(() => {
    // Safely get the icon component or fallback to HelpCircle
    const Icon = (LucideIcons as unknown as Record<string, LucideIcon>)[category.iconName];
    return Icon || LucideIcons.HelpCircle;
  }, [category.iconName]);
  const [imageError, setImageError] = useState(false);

  return (
    <button
      onClick={onClick}
      className="group w-full h-40 bg-white rounded-3xl relative overflow-hidden transition-all duration-200 ease-out
                 shadow-3d hover:shadow-3d-hover active:scale-[0.96] active:shadow-sm
                 flex flex-col items-center justify-center border-b-4 border-gray-100 active:border-b-0 active:translate-y-1"
    >
      {/* Decorative background blob */}
      <div className="absolute top-[-20%] right-[-20%] w-24 h-24 bg-gray-50 rounded-full blur-2xl group-hover:bg-blue-50 transition-colors" />

      <div className="relative z-10 mb-3 p-4 rounded-2xl bg-gradient-to-br from-blue-50 to-white shadow-sm group-hover:from-primary group-hover:to-blue-600 transition-all duration-300">
        {category.iconAsset && !imageError ? (
            <img 
                src={category.iconAsset} 
                alt={category.title}
                className="w-7 h-7 object-contain group-hover:invert transition-all duration-300"
                onError={() => setImageError(true)}
            />
        ) : (
            <IconComponent 
                size={28} 
                className="text-primary group-hover:text-white transition-colors duration-300" 
                strokeWidth={2}
            />
        )}
      </div>
      
      <span className="relative z-10 text-sm font-bold text-gray-800 group-hover:text-primary transition-colors px-2 text-center leading-tight">
        {category.title}
      </span>
    </button>
  );
};